import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, apiRoutes } from '../../api';
import '@fontsource/vazirmatn';
import { scoreListings } from './SortingListings';
import { rankListingsByAiMatch } from './aiSearchSync';
import './mainPage.css';

function buildApiParams(filters) {
    const params = {};
    if (filters.listingType && filters.listingType !== 'all')
        params.listing_type = filters.listingType.toUpperCase();
    if (filters.propertyType && filters.propertyType !== 'all' && !Array.isArray(filters.propertyType))
        params.property_type = filters.propertyType;
    if (filters.usageType && filters.usageType !== 'all' && !Array.isArray(filters.usageType))
        params.usage = filters.usageType;

    // geoFilters multi-select: send first match per type to backend for a server-side pre-filter
    const geoFilters = filters.geoFilters || [];
    const cityIds = geoFilters.filter(f => f.type === 'city').map(f => f.id);
    const regionIds = geoFilters.filter(f => f.type === 'region').map(f => f.id);
    const neighborhoodIds = geoFilters.filter(f => f.type === 'neighborhood').map(f => f.id);
    if (cityIds.length > 0) params.city = cityIds.join(',');
    if (regionIds.length > 0) params.region = regionIds.join(',');
    if (neighborhoodIds.length > 0) params.neighborhood = neighborhoodIds.join(',');

    if (filters.minArea > 0) params.min_area = filters.minArea;
    if (filters.maxArea > 0) params.max_area = filters.maxArea;
    if (filters.listingType === 'rent') {
        if (filters.totalBudget > 0) params.max_total_cost = filters.totalBudget;
        if (filters.depositBudgetMax > 0) params.max_deposit = filters.depositBudgetMax;
        if (filters.rentBudgetMax > 0) params.max_rent = filters.rentBudgetMax;
    }
    if (filters.listingType === 'sale' || filters.listingType === 'auction' || filters.listingType === 'presale') {
        if (filters.maxPrice > 0) params.max_price = filters.maxPrice;
    }
    const checkedAmenities = Object.entries(filters.amenities || {})
        .filter(([, v]) => v === true)
        .map(([id]) => id);
    if (checkedAmenities.length > 0) params.amenity_ids = checkedAmenities.join(',');
    return params;
}

// ── ⑦ Skeleton card ────────────────────────────────────────────
function SkeletonCard() {
    return (
        <div className="skeleton-card">
            <div className="skeleton-img" />
            <div className="skeleton-body">
                <div className="skeleton-line medium" />
                <div className="skeleton-line price" />
                <div className="skeleton-line short" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 4 }}>
                    <div className="skeleton-line full" />
                    <div className="skeleton-line full" />
                    <div className="skeleton-line full" />
                    <div className="skeleton-line full" />
                </div>
            </div>
        </div>
    );
}

// ── ⑦ Empty state ──────────────────────────────────────────────
function EmptyState({ hasActiveFilters, onClearFilters }) {
    return (
        <div className="listing-empty-state">
            <div className="listing-empty-icon">🏘️</div>
            <p className="listing-empty-title">
                {hasActiveFilters ? 'ملکی با این فیلترها یافت نشد' : 'ملکی در این محدوده وجود ندارد'}
            </p>
            <p className="listing-empty-subtitle">
                {hasActiveFilters
                    ? 'فیلترها را تغییر دهید یا برای نتایج بیشتر آن‌ها را پاک کنید.'
                    : 'روی نقشه جابه‌جا شوید یا زوم را کم کنید تا ملک‌های بیشتری ببینید.'}
            </p>
            {hasActiveFilters && (
                <button className="listing-empty-action" onClick={onClearFilters}>
                    پاک کردن فیلترها
                </button>
            )}
        </div>
    );
}

function ListingDtl({
    visibleProperties,
    filterProperties,
    setViewState,
    setSelectedProperty,
    setProperties,
    setError,
    filters,
    onClearFilters,
    sortPriorities = [],
    aiMatch,
    selectedProperty,
    retryKey = 0,
}) {
    const navigate = useNavigate();
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const filterKey = JSON.stringify(filters || {});
    const aiRevision = aiMatch?.revision;
    const aiListingIds = aiMatch?.active
        ? aiMatch.results.map(item => item.listing_id).join(',')
        : '';

    useEffect(() => {
        const controller = new AbortController();
        const timer = window.setTimeout(async () => {
            setLoading(true);
            try {
                const params = aiMatch?.active
                    ? { listing_ids: aiListingIds }
                    : buildApiParams(filters || {});
                const response = await api.get(apiRoutes.listings.publicList, {
                    params,
                    signal: controller.signal,
                });
                setProperties(response.data || []);
                setError(null);
                setHasLoaded(true);
            } catch (err) {
                if (err.code === 'ERR_CANCELED' || controller.signal.aborted) return;

                setError(`خطا در دریافت اطلاعات: ${err.response?.data?.detail || err.message}`);
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        }, hasLoaded ? 350 : 0);

        return () => {
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [filterKey, aiRevision, aiListingIds, retryKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchFavorites = async () => {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        try {
            const response = await api.get(apiRoutes.users.favorites, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setFavorites(response.data.map(fav => fav.id));
        } catch (err) {

        }
    };

    const handleToggleFavorite = async (listingId) => {
        const token = localStorage.getItem('access_token');
        if (!token) { setFeedback('برای ذخیره ملک ابتدا وارد حساب شوید.'); return; }
        const wasFavorite = favorites.includes(listingId);
        setFavorites(prev => wasFavorite ? prev.filter(id => id !== listingId) : [...prev, listingId]);
        try {
            if (wasFavorite) {
                await api.delete(apiRoutes.users.favorite(listingId), {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                await api.post(apiRoutes.users.favorites, { listing_id: listingId }, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }
            setFeedback(wasFavorite ? 'از ذخیره‌شده‌ها حذف شد.' : 'ملک ذخیره شد.');
        } catch (err) {

            setFavorites(prev => wasFavorite ? [...prev, listingId] : prev.filter(id => id !== listingId));
            setFeedback('ذخیره ملک انجام نشد. دوباره تلاش کنید.');
        }
    };

    useEffect(() => {
        fetchFavorites();
    }, []);

    const filteredListings = filterProperties(visibleProperties);
    const displayedListings = aiMatch?.active
        ? rankListingsByAiMatch(filteredListings, aiMatch)
        : scoreListings(filteredListings, sortPriorities);

    // ── ⑦ Check if any filters are active (for empty-state messaging) ──
    const hasActiveFilters = Object.entries(filters || {}).some(([key, val]) => {
        if (['baseMap', 'overlayLayers'].includes(key)) return false;
        if (key === 'amenities' || key === 'geoFeatures') return Object.keys(val || {}).length > 0;
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === 'number') return val > 0;
        if (typeof val === 'string') return val !== '' && val !== 'all';
        return false;
    });

    // ── ⑦ Skeleton loading state ───────────────────────────────
    if (loading && !hasLoaded) {
        return (
            <div className="listing-container">
                <div className="properties-grid">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <SkeletonCard key={i} />
                    ))}
                </div>
            </div>
        );
    }

    // ── ⑦ Empty state ──────────────────────────────────────────
    if (displayedListings.length === 0) {
        return (
            <div className="listing-container">
                <EmptyState
                    hasActiveFilters={hasActiveFilters}
                    onClearFilters={onClearFilters}
                />
            </div>
        );
    }

    return (
        <div className="listing-container">
            {loading && <div className="listing-updating" role="status">در حال به‌روزرسانی نتایج…</div>}
            {feedback && (
                <div className="listing-feedback" role="status">
                    <span>{feedback}</span>
                    <button type="button" onClick={() => setFeedback(null)} aria-label="بستن پیام">×</button>
                </div>
            )}
            <div className="properties-grid">
                {displayedListings.map(listing => {
                    const prop = listing.property || {};
                    const location = prop.location;
                    const isRent = listing.listing_type === 'RENT';
                    const isAuction = listing.listing_type === 'AUCTION';
                    const isPresale = listing.listing_type === 'PRESALE';
                    const price = parseFloat(listing.price);
                    const deposit = parseFloat(listing.deposit);

                    return (
                        <div
                            key={listing.listing_id}
                            className={`property-card ${selectedProperty?.listing_id === listing.listing_id ? 'selected' : ''}`}
                            role="button"
                            tabIndex="0"
                            aria-label={`نمایش ${prop.name || 'ملک'} روی نقشه`}
                            onClick={() => {
                                if (location?.coordinates) {
                                    const [lon, lat] = location.coordinates;
                                    setViewState(prev => ({ ...prev, longitude: lon, latitude: lat, zoom: 15 }));
                                    setSelectedProperty(listing);
                                }
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    event.currentTarget.click();
                                }
                            }}
                        >
                            {/* Image */}
                            <div className="property-image">
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleToggleFavorite(listing.listing_id); }}
                                    className="favorite-button"
                                    aria-label={favorites.includes(listing.listing_id) ? 'حذف از نشان‌شده‌ها' : 'افزودن به نشان‌شده‌ها'}
                                >
                                    {favorites.includes(listing.listing_id) ? '❤️' : '🤍'}
                                </button>
                                <img
                                    src={listing.primary_image?.url || 'default-property-image.jpg'}
                                    alt={prop.name || 'تصویر ملک'}
                                    loading="lazy"
                                />
                            </div>

                            {/* ── ⑥ Details: price first, secondary info muted ── */}
                            <div className="property-details">
                                <div>
                                    {listing.ai_match_rank && (
                                        <div className="property-ai-match">
                                            <span>انتخاب هوشمند #{listing.ai_match_rank}</span>
                                            <span>{listing.ai_match_rank <= 3 ? 'تطابق عالی' : listing.ai_match_rank <= 10 ? 'تطابق بالا' : 'تطابق مناسب'}</span>
                                        </div>
                                    )}
                                    {/* Listing type chip */}
                                    <span className={`property-listing-type-chip ${isRent ? 'rent' : isAuction ? 'auction' : isPresale ? 'presale' : 'sale'}`}>
                                        {isRent ? 'رهن و اجاره' : isAuction ? 'مزایده' : isPresale ? 'پیش‌فروش' : 'فروش'}
                                    </span>

                                    {/* Name */}
                                    <h3 className="property-title">{prop.name || 'بدون نام'}</h3>

                                    {/* ── Price — large and prominent ── */}
                                    {isRent ? (
                                        <>
                                            <p className="property-price-primary">
                                                {price ? `${price.toLocaleString('fa-IR')} ${listing.currency === 'IRR' ? 'ریال' : listing.currency}/ماه` : '—'}
                                            </p>
                                            {deposit > 0 && (
                                                <p className="property-price-secondary">
                                                    ودیعه: {deposit.toLocaleString('fa-IR')} {listing.currency === 'IRR' ? 'ریال' : listing.currency}
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <p className="property-price-primary">
                                            {price ? `${price.toLocaleString('fa-IR')} ${listing.currency === 'IRR' ? 'ریال' : listing.currency}${isAuction ? ' (پایه)' : ''}` : '—'}
                                        </p>
                                    )}

                                    {/* ── Secondary info — muted ── */}
                                    <div className="property-info-grid">
                                        {prop.area && (
                                            <p className="property-info-item">📐 {prop.area} م²</p>
                                        )}
                                        {prop.city && (
                                            <p className="property-info-item">📍 {prop.city}</p>
                                        )}
                                        {prop.neighborhood && (
                                            <p className="property-info-item" style={{ gridColumn: '1 / -1' }}>
                                                {prop.neighborhood}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <button
                                    className="property-details-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/listing/${listing.listing_id}`);
                                    }}
                                >
                                    مشاهده جزئیات
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default ListingDtl;
